import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Calendar, FileText, Activity, AlertCircle, Save } from 'lucide-react';

export interface CampaignSettingsModalProps {
  open: boolean;
  onClose: () => void;
  campaignName: string;
}

/**
 * Campaign Settings Modal
 *
 * Allows users to view and modify campaign settings including:
 * - Campaign name and metadata
 * - Status (planning, in_progress, completed, archived)
 * - Date ranges
 * - Notes and descriptions
 *
 * This is a simplified version - full campaign management features
 * will be added in future iterations.
 */
export function CampaignSettingsModal({
  open,
  onClose,
  campaignName,
}: CampaignSettingsModalProps) {
  // Local state for form fields (will be connected to Zustand store later)
  const [name, setName] = useState(campaignName);
  const [status, setStatus] = useState<'planning' | 'in_progress' | 'completed' | 'archived'>('planning');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      // TODO: Connect to campaign store when implemented
      // await campaignStore.updateCampaign({ name, status, startDate, endDate, description });

      console.log('Campaign settings saved:', {
        name,
        status,
        startDate,
        endDate,
        description,
      });

      // Close modal on success
      onClose();
    } catch (error) {
      console.error('Failed to save campaign settings:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form to original values
    setName(campaignName);
    setStatus('planning');
    setStartDate('');
    setEndDate('');
    setDescription('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title="Campaign Settings" size="lg">
      <div className="space-y-6">
        {/* Campaign Name */}
        <div>
          <label htmlFor="campaign-name" className="flex items-center gap-2 text-sm font-medium text-dark-text mb-2">
            <FileText size={16} className="text-brand-500" />
            Campaign Name
          </label>
          <input
            id="campaign-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-2 bg-dark-surface border border-dark-border/30 rounded-xl text-white placeholder-dark-muted focus:outline-none focus:ring-2 focus:ring-brand-400"
            placeholder="Enter campaign name..."
            data-testid='unknown-enter-campaign-name-input'
            data-ux='enter-campaign-name-edit'
            data-interaction-id='unknown-enter-campaign-name-input' />
        </div>

        {/* Status */}
        <div>
          <label htmlFor="campaign-status" className="flex items-center gap-2 text-sm font-medium text-dark-text mb-2">
            <Activity size={16} className="text-brand-500" />
            Campaign Status
          </label>
          <select
            id="campaign-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
            className="w-full px-4 py-2 bg-dark-surface border border-dark-border/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer"
            data-testid='unknown-field-select'
            data-ux='select'
            data-interaction-id='unknown-field-select'>
            <option value="planning">Planning</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="start-date" className="flex items-center gap-2 text-sm font-medium text-dark-text mb-2">
              <Calendar size={16} className="text-brand-500" />
              Start Date
            </label>
            <input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 bg-dark-surface border border-dark-border/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-brand-400"
              data-testid='unknown-field-input'
              data-ux='field-edit'
              data-interaction-id='unknown-field-input' />
          </div>
          <div>
            <label htmlFor="end-date" className="flex items-center gap-2 text-sm font-medium text-dark-text mb-2">
              <Calendar size={16} className="text-brand-500" />
              End Date
            </label>
            <input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 bg-dark-surface border border-dark-border/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-brand-400"
              data-testid='unknown-field-input'
              data-ux='field-edit'
              data-interaction-id='unknown-field-input' />
          </div>
        </div>

        {/* Description/Notes */}
        <div>
          <label htmlFor="description" className="flex items-center gap-2 text-sm font-medium text-dark-text mb-2">
            <FileText size={16} className="text-brand-500" />
            Description & Notes
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full px-4 py-2 bg-dark-surface border border-dark-border/30 rounded-xl text-white placeholder-dark-muted focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
            placeholder="Add campaign description, goals, or notes..."
            data-testid='unknown-add-campaign-description-goals-or-notes-textarea'
            data-ux='add-campaign-description-goals-or-notes-edit'
            data-interaction-id='unknown-add-campaign-description-goals-or-notes-textarea' />
        </div>

        {/* Info Box */}
        <div className="flex items-start gap-3 p-4 bg-blue-900/20 border border-brand-500/50 rounded-lg">
          <AlertCircle size={20} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-dark-text">
            <p className="font-medium text-blue-400 mb-1">Campaign Settings</p>
            <p className="text-dark-muted">
              These settings control the campaign metadata and organization. Full campaign management features
              including organization selection, route planning, and execution tracking are available in the
              main Campaign Management view.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-dark-border/30">
          <button
            type="button"
            onClick={handleClose}
            className="px-6 py-2 text-sm font-medium text-dark-text bg-dark-surface border border-dark-border/30 rounded-xl hover:bg-dark-input transition-all duration-150"
            disabled={isSubmitting}
            data-testid='unknown-cancel-btn'
            data-ux='cancel'
            data-interaction-id='unknown-cancel-btn'>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSubmitting || !name.trim()}
            className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
            data-testid='unknown-save-btn'
            data-ux='save'
            data-interaction-id='unknown-save-btn'>
            <Save size={16} />
            {isSubmitting ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
